import type { ActivePlanState } from "../../session-logic";
import { i18n } from "../../i18n";
import { CircleCheckIcon, CircleIcon, Loader2Icon } from "lucide-react";
import { memo, useId, useState } from "react";
import { cn } from "~/lib/utils";

export function activeTaskProgressCardKey(
  threadKey: string,
  turnId: ActivePlanState["turnId"],
): string {
  return `${threadKey}:${turnId}`;
}

function currentStepNumber(steps: ActivePlanState["steps"]): number {
  const inProgressIndex = steps.findIndex((step) => step.status === "inProgress");
  if (inProgressIndex >= 0) return inProgressIndex + 1;

  const pendingIndex = steps.findIndex((step) => step.status === "pending");
  return pendingIndex >= 0 ? pendingIndex + 1 : steps.length;
}

function statusLabel(status: ActivePlanState["steps"][number]["status"]): string {
  switch (status) {
    case "completed":
      return i18n.t("tasks.status.completed");
    case "inProgress":
      return i18n.t("tasks.status.inProgress");
    case "pending":
      return i18n.t("tasks.status.pending");
  }
}

export const ActiveTaskProgressCard = memo(function ActiveTaskProgressCard({
  plan,
}: {
  readonly plan: ActivePlanState;
}) {
  const taskListId = useId();
  const [expanded, setExpanded] = useState(false);
  const current = currentStepNumber(plan.steps);
  const progressLabel = i18n.t("tasks.progress", {
    current,
    total: plan.steps.length,
  });
  const stepOccurrences = new Map<string, number>();

  return (
    <div
      className="relative mx-auto mb-2 flex w-full max-w-[320px] justify-center"
      data-active-task-progress="true"
    >
      <div
        id={taskListId}
        hidden={!expanded}
        role="region"
        aria-label={i18n.t("tasks.accessibility.summary", { progress: progressLabel })}
        tabIndex={0}
        className="absolute inset-x-0 bottom-[calc(100%+0.375rem)] z-10 max-h-[106px] w-full overflow-y-auto overscroll-contain rounded-xl border border-border/70 bg-card px-2.5 py-1 shadow-sm [-ms-overflow-style:none] [scrollbar-width:none] focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 [&::-webkit-scrollbar]:hidden"
      >
        {expanded ? (
          <div role="list">
            {plan.steps.map((step) => {
              const label = statusLabel(step.status);
              const occurrence = (stepOccurrences.get(step.step) ?? 0) + 1;
              stepOccurrences.set(step.step, occurrence);
              return (
                <div
                  key={`${step.step}:${occurrence}`}
                  role="listitem"
                  className="flex min-h-6 items-start gap-2"
                  aria-label={i18n.t("tasks.accessibility.step", {
                    step: step.step,
                    status: label,
                  })}
                  data-task-status={step.status}
                >
                  {step.status === "inProgress" ? (
                    <Loader2Icon
                      aria-hidden="true"
                      className="mt-1.5 size-3 shrink-0 animate-spin text-foreground motion-reduce:animate-none"
                      strokeWidth={2}
                    />
                  ) : step.status === "completed" ? (
                    <CircleCheckIcon
                      aria-hidden="true"
                      className="mt-1.5 size-3 shrink-0 text-foreground"
                      strokeWidth={2}
                    />
                  ) : (
                    <CircleIcon
                      aria-hidden="true"
                      className="mt-1.5 size-3 shrink-0 text-foreground"
                      strokeWidth={2}
                    />
                  )}
                  <span
                    className={cn(
                      "min-w-0 break-words text-sm leading-6 tracking-tight",
                      step.status === "completed"
                        ? "text-muted-foreground/65"
                        : "text-muted-foreground",
                    )}
                  >
                    {step.step}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={taskListId}
        aria-label={i18n.t(expanded ? "tasks.collapse" : "tasks.expand")}
        onClick={() => setExpanded((currentExpanded) => !currentExpanded)}
        className="flex min-h-9 cursor-pointer items-center gap-2 rounded-full border border-border/60 bg-card px-3.5 py-1.5 text-sm font-medium text-muted-foreground shadow-xs tabular-nums hover:border-border hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2"
      >
        <CircleIcon aria-hidden="true" className="size-3.5 text-primary/35" strokeWidth={2.5} />
        <span>{progressLabel}</span>
      </button>
    </div>
  );
});
